import { useCallback, useState } from 'react'

import { copy, LunyaMascot } from '~/modules/lunya'
import { notifications } from '~/shared/lib/notifications'
import { usePlatform } from '~/shared/platform'
import { Button, Screen, Text, VStack } from '~/shared/ui'

import { AccessStatusQuery } from '../../api'
import { GATE_MASCOT_SIZE } from '../../config'

import styles from './ChannelGateScreen.module.scss'

/**
 * Экран гейта подписки на канал.
 *
 * Выхода отсюда нет намеренно: ни кнопки «назад», ни нативного TG BackButton, ни меню-острова
 * (страница живёт вне TabsLayout). Любой другой маршрут роутер уводит сюда же, пока бэк
 * не ответит «доступ есть» — это и есть требование гейта.
 *
 * Возврат в приложение происходит сам: `AccessStatusQuery` перепроверяет статус фоном и при
 * возврате в аппу, а роутер снимает редирект, как только доступ появился. Кнопка «Я подписалась»
 * — та же перепроверка, только вручную и сразу.
 */
export function ChannelGateScreen() {
  const platform = usePlatform()
  const { data, refetch } = AccessStatusQuery.useQuery()
  const [isChecking, setIsChecking] = useState(false)

  const channelUrl = data?.channelUrl

  const handleSubscribe = useCallback(() => {
    if (!channelUrl) return
    platform.haptic.impact('light')
    platform.share.openTelegramLink(channelUrl)
  }, [channelUrl, platform])

  const handleCheck = useCallback(async () => {
    setIsChecking(true)
    try {
      const { data: fresh } = await refetch()
      if (fresh?.isAllowed) {
        platform.haptic.notification('success')
        return
      }
      // Не нашли — говорим об этом здесь же. Уводить некуда: экран и так последний.
      notifications.info(copy.gate.not_found)
    } finally {
      setIsChecking(false)
    }
  }, [platform, refetch])

  return (
    <Screen background='FLOW' hasStickyCta>
      <Screen.Content>
        <VStack gap='MEDIUM_S'>
          <div className={styles.mascot}>
            <LunyaMascot pose='ASKING' size={GATE_MASCOT_SIZE} hasGlow />
          </div>
          <Text variant='H1' align='CENTER'>
            {copy.gate.title}
          </Text>
          <Text variant='BODY_L' color='SECONDARY' align='CENTER'>
            {channelUrl ? copy.gate.body : copy.gate.no_link}
          </Text>
        </VStack>
      </Screen.Content>
      <Screen.Footer>
        <VStack gap='SMALL_S'>
          {channelUrl ? (
            <Button
              variant='PRIMARY'
              width='FULL'
              text={copy.gate.subscribe}
              onPress={handleSubscribe}
            />
          ) : null}
          <Button
            variant='SECONDARY'
            width='FULL'
            text={copy.gate.check}
            isLoading={isChecking}
            onPress={() => void handleCheck()}
          />
        </VStack>
      </Screen.Footer>
    </Screen>
  )
}
